import { CSSProperties, FC, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { Button } from "@src/lib/ui/Button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@src/lib/ui/Tooltip";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@src/lib/ui/Dialog";
import XIcon from "@src/lib/icons/12px/x";
import ArrowsExpandIcon from "@src/lib/icons/18px/arrows-expand";
import MediaPauseOutlineIcon from "@src/lib/icons/18px/media-pause-outline";
import MediaPlayFillIcon from "@src/lib/icons/18px/media-play-fill";
import { ReleaseHeadlineFeature } from "./releases";

// A feature's clip, as on the changelog release page. It plays silently
// (looping unless `loop` is off) unless the viewer prefers reduced motion or
// paused an earlier clip; clicking it opens a full-size lightbox with
// playback controls.
const LeadVideo: FC<{
  feature: ReleaseHeadlineFeature;
  // Start paused (the viewer paused the clip before this one).
  startPaused?: boolean;
  // The viewer pressed pause or play.
  onPausedChange?: (paused: boolean) => void;
  loop?: boolean;
  // The clip played to its end (only without `loop`).
  onEnded?: () => void;
  // How far through the clip it is, 0–1.
  onProgress?: (fraction: number) => void;
}> = ({
  feature,
  startPaused = false,
  onPausedChange,
  loop = true,
  onEnded,
  onProgress,
}) => {
  const reduceMotion = useReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);
  // Set from the video's own play/pause events, so it stays right when the
  // browser blocks autoplay (e.g. Low Power Mode).
  const [playing, setPlaying] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const resumeOnClose = useRef(false);
  if (!feature.video) {
    return null;
  }
  const sources = {
    src: `${feature.video.url}.mp4`,
    poster: `${feature.video.url}.jpg`,
    width: feature.video.width,
    height: feature.video.height,
  };
  const togglePlaying = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (video.paused) {
      video.play().catch(() => setPlaying(false));
      onPausedChange?.(false);
    } else {
      video.pause();
      onPausedChange?.(true);
    }
  };
  // Only one copy plays at a time: pause the loop while the lightbox is open,
  // and resume it afterwards unless the viewer had paused it.
  const setLightboxOpen = (open: boolean) => {
    setExpanded(open);
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (open) {
      resumeOnClose.current = !video.paused;
      video.pause();
    } else if (resumeOnClose.current) {
      video.play().catch(() => setPlaying(false));
    }
  };
  return (
    <div className="relative">
      <Dialog open={expanded} onOpenChange={setLightboxOpen}>
        <DialogTrigger asChild>
          <button
            type="button"
            aria-label={`Watch the ${feature.title} demo full size`}
            data-testid="release-lead-video"
            className="group block w-full cursor-zoom-in overflow-hidden rounded-xl focus-visible:ring-1 focus-visible:ring-border-tertiary focus-visible:outline-none"
          >
            <video
              ref={videoRef}
              {...sources}
              autoPlay={!reduceMotion && !startPaused}
              muted
              loop={loop}
              playsInline
              aria-hidden
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={onEnded}
              onTimeUpdate={(event) => {
                const { currentTime, duration } = event.currentTarget;
                if (duration) onProgress?.(currentTime / duration);
              }}
              className="h-auto w-full border border-border-primary bg-bg-primary"
            />
            <span className="absolute right-2 bottom-2 flex size-8 items-center justify-center rounded-lg bg-bg-elevated text-icon-primary opacity-0 shadow-default transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
              <ArrowsExpandIcon aria-hidden />
            </span>
          </button>
        </DialogTrigger>
        <DialogContent
          hideDefaultTitle
          hideCloseButton
          aria-describedby={undefined}
          data-testid="release-lead-video-lightbox"
          // Fit the viewport both ways so the controls stay on screen.
          style={
            {
              "--video-aspect": feature.video.width / feature.video.height,
            } as CSSProperties
          }
          className="w-[min(1200px,calc(100vw-32px),calc((100dvh-32px)*var(--video-aspect)))] max-w-none overflow-hidden p-0"
        >
          <DialogTitle className="sr-only">{feature.title} demo</DialogTitle>
          <video
            {...sources}
            autoPlay={!reduceMotion}
            controls
            muted
            loop
            playsInline
            className="h-auto w-full bg-bg-primary"
          />
          {/* Sits on the video's corner, so it carries its own surface. */}
          <DialogClose className="absolute top-3 right-3 flex size-8 items-center justify-center rounded-full bg-bg-elevated text-icon-secondary shadow-default transition-colors outline-none hover:bg-bg-secondary hover:text-icon-primary focus-visible:ring-1 focus-visible:ring-brand-border">
            <XIcon className="size-3" aria-hidden />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogContent>
      </Dialog>
      {/* The loop runs longer than 5 seconds, so it needs a pause control. */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="icon-sm"
            onClick={togglePlaying}
            aria-label={playing ? "Pause video" : "Play video"}
            className="absolute bottom-2 left-2"
          >
            {playing ? <MediaPauseOutlineIcon /> : <MediaPlayFillIcon />}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          {playing ? "Pause video" : "Play video"}
        </TooltipContent>
      </Tooltip>
    </div>
  );
};

export default LeadVideo;
